import React, { useState, useRef, useCallback } from "react";
import { Camera, X, Upload, Image as ImageIcon } from "lucide-react";

const ImageUploader = ({
  variant = "logo",
  value = null,
  onChange,
  onRemove,
  disabled = false,
  helperText,
  maxSizeMB = 5,
  acceptedTypes = ["image/jpeg", "image/png", "image/webp"],
  className = "",
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const aspectRatioClass = variant === "logo" 
    ? "aspect-square w-32 h-32" 
    : "aspect-video w-full max-w-md h-48";

  const getImageUrl = useCallback(() => {
    if (!value) return null;
    if (typeof value === "string") return value;
    if (value instanceof File) return URL.createObjectURL(value);
    if (value?.file instanceof File) return URL.createObjectURL(value.file);
    if (value?.file && typeof value.file === "string") return value.file;
    if (value?.fileUrl) return value.fileUrl;
    if (value?.url) return value.url;
    return null;
  }, [value]);

  const imageUrl = getImageUrl();

  const validateFile = (file) => {
    if (!acceptedTypes.includes(file.type)) {
      setError("Please upload a valid image file (JPG, PNG, or WebP)");
      return false;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`File size must be less than ${maxSizeMB}MB`);
      return false;
    }
    setError(null);
    return true;
  };

  const handleFileSelect = (file) => {
    if (file && validateFile(file)) {
      onChange({
        file,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;
    
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleClick = () => {
    if (!disabled) fileInputRef.current?.click();
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setError(null);
    if (onRemove) onRemove();
    onChange(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div className={`${className}`}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative group cursor-pointer
          ${aspectRatioClass}
          rounded-xl overflow-hidden
          transition-all duration-200 ease-in-out
          ${isDragging 
            ? "ring-2 ring-primary-500 ring-offset-2 bg-primary-50" 
            : "ring-1 ring-grey-200 hover:ring-2 hover:ring-grey-300"
          }
          ${disabled ? "opacity-50 cursor-not-allowed" : ""}
          ${!imageUrl ? "bg-grey-50" : ""}
        `}
      >
        {imageUrl ? (
          <>
            <img
              src={imageUrl}
              alt="Upload preview"
              className="w-full h-full object-cover"
            />
            {!disabled && (
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={handleClick}
                  className="p-2 bg-white rounded-full shadow-lg hover:bg-grey-100 transition-colors"
                  title="Change image"
                >
                  <Camera className="w-5 h-5 text-grey-700" />
                </button>
                <button
                  type="button"
                  onClick={handleRemove}
                  className="p-2 bg-white rounded-full shadow-lg hover:bg-red-50 transition-colors"
                  title="Remove image"
                >
                  <X className="w-5 h-5 text-red-600" />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
            <div className={`
              p-3 rounded-full mb-3
              ${isDragging ? "bg-primary-100" : "bg-grey-100"}
              transition-colors duration-200
            `}>
              {isDragging ? (
                <Upload className="w-6 h-6 text-primary-600" />
              ) : (
                <ImageIcon className="w-6 h-6 text-grey-400" />
              )}
            </div>
            <p className="text-sm font-medium text-grey-700">
              {isDragging ? "Drop image here" : "Click or drag to upload"}
            </p>
            <p className="text-xs text-grey-500 mt-1">
              {variant === "logo" ? "Square image works best" : "16:9 ratio recommended"}
            </p>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes.join(",")}
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled}
        />
      </div>

      {error && (
        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
          <X className="w-4 h-4" />
          {error}
        </p>
      )}

      {helperText && !error && (
        <p className="mt-2 text-xs text-grey-500">{helperText}</p>
      )}

      {value && !error && (
        <p className="mt-2 text-xs text-grey-500 flex items-center gap-1">
          <ImageIcon className="w-3 h-3" />
          {value?.fileName || "Image selected"}
        </p>
      )}
    </div>
  );
};

export default ImageUploader;
